import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { Observable } from 'rxjs';

import { TokenService } from '@services/token.service';
import { CompaniesService } from '@services/companies.service';
import { enumUserRole } from '@data-layer/enums/user';
import { GoogleAnalyticsReportService } from '@services/google-analytics-report.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { ApplicationModeService } from '@services/application-mode.service';
import { cloneDeep, isBoolean } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
  private readonly routesToRedirect = [
    {
      // prettier-ignore
      match: /^\/?entry-summary\/ES\d{7}/gm,
      findVariables: (url: string): any => ({ referenceNumber: url.match(/ES\d{7}/gm)[0] }),
      redirect: (data: any) => `/entry-summaries-management?referenceNumber=${data.referenceNumber}`,
    },
    {
      // prettier-ignore
      match: /^\/?isf\/ISF\d{7}/gm,
      findVariables: (url: string): any => ({ referenceNumber: url.match(/ISF\d{7}/gm)[0] }),
      redirect: (data: any) => `/isfs-management?referenceNumber=${data.referenceNumber}`,
    },
  ];
  constructor(
    private tokenService: TokenService,
    private companiesService: CompaniesService,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsReportService: GoogleAnalyticsReportService,
    private applicationModeService: ApplicationModeService,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    const switchRole: boolean = !!route.queryParams.switchRole;
    const roles = this.tokenService.getRoles();
    const activeApplicationMode = this.applicationModeService.activeApplicationMode;
    const requiredRoles: enumUserRole[] = route.data.roles;
    const isOnlyActiveRole: boolean = isBoolean(route.data.isOnlyActiveRole) ? route.data.isOnlyActiveRole : true;
    if (roles) {
      if (switchRole) {
        const newQueryParams = cloneDeep(route.queryParams);
        delete newQueryParams.switchRole;
        this.router.navigate([state.url.split('?')[0]], { queryParams: newQueryParams });
        if (requiredRoles.some((requiredRole) => roles.includes(requiredRole))) {
          if (!this.applicationModeService.isActiveApplicationModeFitToRoles(requiredRoles)) {
            this.applicationModeService.setActiveApplicationModeByRole(requiredRoles[0]);
          }
          return true;
        }
      }

      if (
        isOnlyActiveRole
          ? this.applicationModeService.isActiveApplicationModeFitToRoles(requiredRoles)
          : requiredRoles && requiredRoles.filter((role) => roles.includes(role)).length
      ) {
        return true;
      } else {
        if (activeApplicationMode === enumApplicationMode.SUPPORT && this.routesToRedirect.some((item) => state.url.match(item.match))) {
          const matchedRoute = this.routesToRedirect.find((item) => state.url.match(item.match));
          const variables = matchedRoute.findVariables(state.url);
          this.router.navigateByUrl(matchedRoute.redirect(variables));
          return;
        }
        const routes = this.getHomePathByApplicationMode(activeApplicationMode);
        this.router.navigate(routes);
        return false;
      }
    }
    this.tokenService.eraseToken();
    this.googleAnalyticsReportService.logout();
    return false;
  }

  // public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
  //   return false;
  // }

  public canLoad(route: Route, segment: UrlSegment[]) {
    return true;
  }

  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivateCustom();
  }

  private canActivateCustom(): boolean {
    if (!this.tokenService.isAuthenticated) {
      this.tokenService.eraseToken();
      this.googleAnalyticsReportService.logout();
      return false;
    }

    return true;
  }

  private getHomePathByApplicationMode(applicationMode: enumApplicationMode) {
    if (applicationMode === enumApplicationMode.SUPPORT) {
      return ['dashboard-management'];
    } else {
      return ['dashboard'];
    }
  }
}
